import { isNumber, isPlainObject, isString } from "lodash-es";
import { DateTime } from "luxon";

import type { MediaRecorderTranscriptTurn } from "@/store/audio/recording/media-types";
import { AssemblyAiTranscriberPayloadType } from "@/store/audio/recording/media-transcriber/transcriber.types";

type AssemblyAiTurnPayload = {
  transcript?: unknown;
  text?: unknown;
  words?: unknown;
  turn_order?: unknown;
  turnOrder?: unknown;
  timestamp?: unknown;
  audio_start?: unknown;
  audio_end?: unknown;
  channel?: unknown;
};

const getString = (value: unknown): string | undefined =>
  isString(value) && value.length ? value : undefined;

const getNumber = (value: unknown): number | undefined => {
  if (!isNumber(value) || Number.isNaN(value)) {
    return undefined;
  }
  return value;
};

export function resolveAssemblyAiRealtimePayloadType({ raw }: { raw: unknown }): string {
  if (!isPlainObject(raw)) return "unknown";
  const rawObject = raw as Record<string, unknown>;
  const candidate = (rawObject.type ?? rawObject.message_type) as unknown;

  switch (candidate) {
    case "Turn":
      return AssemblyAiTranscriberPayloadType.Turn;
    case "Begin":
      return AssemblyAiTranscriberPayloadType.Begin;
    case "Termination":
      return AssemblyAiTranscriberPayloadType.Termination;
    default:
      return AssemblyAiTranscriberPayloadType.Unknown;
  }
}

export function mapAssemblyAiTurnPayloadToRecorderTurn({
  raw,
}: {
  raw: unknown;
}): MediaRecorderTranscriptTurn | null {
  if (!isPlainObject(raw)) return null;
  const payload = raw as AssemblyAiTurnPayload;
  const transcript = getString(payload.transcript) ?? getString(payload.text);
  if (!transcript) return null;

  const channelNumber = getNumber(payload.channel);
  const channel: 0 | 1 = channelNumber === 1 ? 1 : 0;
  const audioStart = getNumber(payload.audio_start);
  const timestamp = getNumber(payload.timestamp);
  const timestampMs = normalizeAudioRecordingTimestamp({
    value: audioStart ?? timestamp,
  });
  const turnOrder = getNumber(payload.turn_order) ?? getNumber(payload.turnOrder) ?? 0;

  const wordsInput = Array.isArray(payload.words) ? payload.words : undefined;
  const words = wordsInput?.map(rawWord => {
    if (!isPlainObject(rawWord)) {
      return {
        word: "",
        startMs: timestampMs,
        endMs: timestampMs,
      };
    }

    const wordRecord = rawWord as Record<string, unknown>;
    const text = getString(wordRecord["text"]) ?? getString(wordRecord["word"]) ?? "";
    const start = getNumber(wordRecord["start"] ?? wordRecord["start_ms"]);
    const end = getNumber(wordRecord["end"] ?? wordRecord["end_ms"]);

    return {
      word: text,
      startMs: normalizeAudioRecordingTimestamp({
        value: start,
      }),
      endMs: normalizeAudioRecordingTimestamp({
        value: end,
      }),
    };
  });

  return {
    channel,
    transcript,
    timestampMs,
    isEndOfTurn:
      (raw as { end_of_turn?: unknown; endOfTurn?: unknown }).end_of_turn === true ||
      (raw as { end_of_turn?: unknown; endOfTurn?: unknown }).endOfTurn === true,
    words,
    raw,
    turnOrder,
  };
}

export function normalizeAudioRecordingTimestamp({ value }: { value: number | undefined }): number {
  // If the value is null or undefined, use the current timestamp.
  if (value == null || Number.isNaN(value)) {
    return DateTime.now().toMillis();
  }

  return Math.round(value);
}
